import React from "react";
import { View, StyleSheet, Image } from "react-native";
import { Spacer } from "../../components/spacer/spacer";
import {
  Avatar,
  useTheme,
  Headline,
  Card,
  Title,
  Paragraph,
  ActivityIndicator,
  Divider,
} from "react-native-paper";
import { isBalanceError, toHumanDollars } from "../../utils/money";
import {
  DomusoProperty,
  DomusoPropertyProfile,
  DomusoPropertyFeeProfile,
} from "../../../types/db-types";
import { ThemeType, theme } from "../../theme/theme";
import { deriveUnitTitle, getPropertyAddressFromPropertyProfile } from "../../utils";
import { Text } from "../text/text";
import { Button, ButtonVariant } from "../button/button";
import { Inline } from "../layout/inline";
import { Stack } from "../layout/stack";
import { BalanceDueUnavailableInfoBox } from "../balance-due-unavailable-info-box";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers";

interface SingleReservation {
  accounting_details: any;
  account_system_type: any;
  property_unit: string;
  prop_name: string;
  prop_id: string;
  balance_due: string;
  allowed_payments: "ANY" | "DO_NOT_ACCEPT_PAYMENT" | "CASH_EQUIVALENT";
  accounting_id: string;
  yardi_id: string;
  id: string;
  status: string;
  is_blacklisted: boolean;
  is_blocked: boolean;
  isPaymentEnabled: boolean;
}
export interface ActiveAutoPay {
  schedule_date: any;
  id: any;
  reservation_id: any;
}
interface ReservationCardProps extends SingleReservation {
  onPress: any;
  navigation: any;
  service: any;
  property: DomusoProperty;
  account_number: string;
  property_profile: DomusoPropertyProfile;
  property_fee_profile: DomusoPropertyFeeProfile;
  active_auto_pay: ActiveAutoPay;
}
export const ReservationCard = (cardProps: ReservationCardProps) => {
  const {
    accounting_details = {},
    account_system_type,
    accounting_id,
    yardi_id,
    account_number,
    active_auto_pay,
    allowed_payments,
    balance_due,
    id,
    is_blocked,
    navigation,
    prop_id,
    is_blacklisted,
    prop_name,
    property,
    property_fee_profile,
    property_profile,
    property_unit,
    isPaymentEnabled,
    status
  } = cardProps;
  const theme = useTheme() as ThemeType;
  const hasBalanceError = isBalanceError(accounting_details.balanceDue);
  const isBlacklisted = useSelector((state: RootState) => state.reservation.isBlacklisted);
  let balanceDue = isNaN(accounting_details.balanceDue)
    ? accounting_details.balanceDue
    : `${toHumanDollars(accounting_details.balanceDue)}`;
  const { spacing, colors } = theme;
  const styles = StyleSheet.create({
    cardContainer: {
      width: "100%",
      paddingBottom: spacing.medium,
      backgroundColor: colors.white,
      shadowColor: colors.grey,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.9,
      shadowRadius: 2,
      elevation: 1,
    },
    titleContainer: {
      backgroundColor: theme.colors.dim,
      padding: spacing.medium,
    },
    titleStyle: {
      fontSize: theme.fontSize.large,
      fontWeight: "bold",
    },
    subtitleStyle: {
      color: theme.colors.text,
      fontSize: theme.fontSize.large,
    },
    iconContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexDirection: "row",
    },
    imageWidth: {
      height: 20,
      width: 19.67,
      resizeMode: "contain",
    },
    balanceDueText: { textAlign: "left", fontWeight: "600", color: theme.colors.darkBg },
    balanceDueDescription: { textAlign: "left", fontWeight: "600", fontSize: theme.fontSize.small },
    buttonsContainer: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    button: { width: "49%" },
    buttonText: { fontSize: theme.fontSize.medium },
    iconText: {
      textDecorationLine: "underline",
    },
  });
  const title = `${deriveUnitTitle(property_unit)} - ${prop_name}`;

  const renderBalanceDue = () => {
    if (hasBalanceError) {
      return (
        <>
          <Spacer size="tiny" />
          <BalanceDueUnavailableInfoBox />
        </>
      );
    } else {
      return (
        <>
          <Spacer size="medium" />
          <Headline style={styles.balanceDueText}>{balanceDue}</Headline>
          <Text tx="componentStatic.currentBalanceDue" style={styles.balanceDueDescription}></Text>
        </>
      );
    }
  };

  return (
    <View style={styles.cardContainer}>
      <Stack space="tiny" style={styles.titleContainer} useThemeBackgroundColor={false}>
        <Text style={styles.titleStyle}>{title}</Text>
        <Text style={styles.subtitleStyle}>
          {getPropertyAddressFromPropertyProfile(property_profile)}
        </Text>
      </Stack>
      <View style={{ paddingHorizontal: spacing.smaller }}>
        {!["string", "number"].includes(typeof balanceDue) ? (
          <View style={{ flex: 1, flexDirection: "row", justifyContent: "flex-start" }}>
            <Spacer size="medium" />
            <ActivityIndicator
              style={{ textAlign: "left" }}
              animating={true}
              color={theme.colors.error}
            />
          </View>
        ) : (
          renderBalanceDue()
        )}
        <Spacer size="medium" />
        <View style={styles.buttonsContainer}>
          <Button
            tx="componentStatic.makePayment"
            textStyle={styles.buttonText}
            style={styles.button}
            disabled={isBlacklisted || !isPaymentEnabled}
            mode="contained"
            onPress={() =>
              navigation.navigate("Actions", {
                screen: "PaymentFlowPaymentDetails",
                params: {
                  account_number,
                  accountingDetails: accounting_details,
                  allowedPayments: allowed_payments,
                  balanceDue: balance_due,
                  propertyUnit: property_unit,
                  propName: prop_name,
                  reservationId: id,
                  currentProperty: property,
                  currentPropertyProfile: property_profile,
                  currentPropertyFeeProfile: property_fee_profile,
                  activeAutoPay: active_auto_pay,
                  isBlacklisted: is_blacklisted,
                  isBlocked: is_blocked,
                  status: status, 
                },
              })
            }
          />
          <Button
            tx="componentStatic.paymentHistory"
            mode="contained"
            variant={ButtonVariant.LIGHT}
            textStyle={styles.buttonText}
            style={styles.button}
            onPress={() =>
              navigation.navigate("SpecificReservation", {
                account_number,
                accountingId: accounting_id,
                yardiId: yardi_id,
                accountSystemType: account_system_type,
                accountingDetails: accounting_details,
                allowedPayments: allowed_payments,
                balanceDue: balance_due,
                propertyUnit: property_unit,
                propName: prop_name,
                reservationId: id,
                currentProperty: property,
                currentPropertyProfile: property_profile,
                currentPropertyFeeProfile: property_fee_profile,
                activeAutoPay: active_auto_pay,
                isBlacklisted: is_blacklisted,
                isBlocked: is_blocked,
                hasEnabledPaymentMethods: isPaymentEnabled,
              })
            }
          />
        </View>
      </View>
    </View>
  );
};
